import React, { useState, useEffect } from 'react';
import { X, Copy, Mail, Users, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';

const ShareListModal = ({ isOpen, onClose, list }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [memberNames, setMemberNames] = useState([]);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    if (!isOpen || !list) {
      setLoadingMembers(false);
      setMemberNames([]);
      return;
    }

    // Close modal if user logs out
    if (!user) {
      onClose();
      return;
    }

    let isCancelled = false;

    const fetchMemberNames = async () => {
      if (isCancelled) return;
      
      // Don't re-fetch if we already have member data for this list
      if (memberNames.length > 0) {
        return;
      }
      
      setLoadingMembers(true);
      
      if (!list.members || list.members.length === 0) {
        if (!isCancelled) {
          setMemberNames([]);
          setLoadingMembers(false);
        }
        return;
      }

      try {
        const memberPromises = list.members.map(async (memberId) => {
          if (isCancelled) return null;
          
          // If this is the current user, use their auth data directly
          if (user && memberId === user.uid) {
            return {
              uid: memberId,
              displayName: user.displayName || user.email || 'You'
            };
          }
          
          // For other members, fetch from database
          try {
            const userDoc = await getDoc(doc(db, 'users', memberId));
            if (isCancelled) return null;
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                uid: memberId,
                displayName: userData.displayName || userData.email || t('common.unknownUser')
              };
            } else {
              // User document doesn't exist, create a basic one
              try {
                await setDoc(doc(db, 'users', memberId), {
                  uid: memberId,
                  email: null,
                  displayName: null,
                  createdAt: new Date(),
                  preferences: {
                    currency: 'NOK',
                    location: null
                  }
                });
                console.log(`Created missing user document for ${memberId}`);
                return {
                  uid: memberId,
                  displayName: t('common.user')
                };
              } catch (createError) {
                console.error('Failed to create user document:', createError);
                return {
                  uid: memberId,
                  displayName: t('common.unknownUser')
                };
              }
            }
          } catch (memberError) {
            // If it's a permission error, don't log it as it's expected when user logs out
            if (isCancelled) return null;
            return {
              uid: memberId,
              displayName: t('common.unknownUser')
            };
          }
        });

        const members = await Promise.all(memberPromises);
        
        // Filter out null members and ensure we have valid data
        const validMembers = members.filter(member => member && member.uid && member.displayName);
        
        if (!isCancelled) {
          setMemberNames(validMembers);
        }
      } catch (error) {
        // If it's a permission error, don't log it as it's expected when user logs out
        if (!error.message?.includes('permission-denied')) {
          console.error('ShareListModal: Error fetching member names:', error);
        }
        if (!isCancelled) {
          setMemberNames([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingMembers(false);
        }
      }
    };

    fetchMemberNames();

    // Cleanup function to cancel the operation if component unmounts or user logs out
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, list, user]);

  if (!isOpen || !list || !user) return null;

  const shareUrl = `${window.location.origin}/list/${list.id}`;
  const shareText = `${t('shareModal.shareText')} "${list.name}" ${t('shareModal.onApp')}!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t('shareModal.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(t('shareModal.copyFailed'));
    }
  };

  const handleEmailShare = () => {
    const subject = `${t('shareModal.emailSubject')}: ${list.name}`;
    const body = `${shareText}\n\n${shareUrl}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  const displayedMembers = showAllMembers ? memberNames : memberNames.slice(0, 5);
  const hasMoreMembers = memberNames.length > 5;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-2 sm:mx-4 min-h-[300px] sm:min-h-[400px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-primary-600" />
            {t('shareModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">
              {list.name}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">
              {list.description || t('shareModal.noDescription')}
            </p>
          </div>

          {/* Share Link */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('shareModal.shareLink')}
            </label>
            <div className="flex space-x-1 sm:space-x-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="input-field flex-1 text-xs sm:text-sm"
              />
              <button
                onClick={handleCopyLink}
                className="btn-secondary flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-3"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{t('shareModal.copied')}</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{t('shareModal.copy')}</span>
                  </>
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t('shareModal.linkHelp')}
            </p>
          </div>

          {/* Email Share */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('shareModal.shareViaEmail')}
            </label>
            <button
              onClick={handleEmailShare}
              className="w-full btn-secondary flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm py-2"
            >
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{t('shareModal.openEmailClient')}</span>
            </button>
          </div>

          {/* List Members */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('shareModal.currentMembers')} {loadingMembers ? '' : `(${memberNames.length})`}
            </label>
            <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
              {loadingMembers ? (
                <div className="flex flex-wrap gap-2 mb-2">
                  {/* Simulate member tags while loading */}
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-500 animate-pulse">
                    <div className="w-16 h-4 bg-gray-300 rounded"></div>
                  </div>
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-500 animate-pulse">
                    <div className="w-20 h-4 bg-gray-300 rounded"></div>
                  </div>
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-500 animate-pulse">
                    <div className="w-14 h-4 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ) : memberNames.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {displayedMembers.map((member, index) => (
                      <span
                        key={`${member.uid}-${index}`}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 font-medium"
                      >
                        {member.displayName}
                      </span>
                    ))}
                  </div>
                  {hasMoreMembers && (
                    <button
                      onClick={() => setShowAllMembers(!showAllMembers)}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      {showAllMembers ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          {t('shareModal.showLess')}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          {t('shareModal.showMore')} {memberNames.length - 5} {memberNames.length - 5 === 1 ? t('shareModal.member') : t('shareModal.members')}
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No members yet
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <h4 className="text-xs sm:text-sm font-medium text-blue-800 mb-1 sm:mb-2">{t('shareModal.howToShare')}</h4>
            <ul className="text-xs sm:text-sm text-blue-700 space-y-0.5 sm:space-y-1">
              <li>• {t('shareModal.instruction1')}</li>
              <li>• {t('shareModal.instruction2')}</li>
              <li>• {t('shareModal.instruction3')}</li>
              <li>• {t('shareModal.instruction4')}</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-secondary text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareListModal;
